/* @flow */

import { isValid } from '../utils/coreutils';
import { isOptedOut } from '../utils/privacyutils';
import publink from './publinkid';
import a33across from './33acrossidp';
import fabrick from './fabrickidp';
import liverampidp from './liverampidp';
import pubcommonid from './pubcommonid';
import id5idp from './id5idp';

/**
 * @description Implements manager class for UserIDs
 * @class
 * @exports idm
 */
class _idpmanager {
  _doc: Document;

  _win: WindowProxy;

  _enabledIDs: Array<string>;

  _IDProviders: Array<IDPInterface>;

  constructor() {
    this._doc = document;
    this._win = window;
    this._enabledIDs = [];
    this._IDProviders = [
      liverampidp,
      fabrick,
      pubcommonid,
      a33across,
      id5idp,
      publink
    ];
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._doc = d;
    this._win = w;
    // init all children
    this._IDProviders.forEach(idp => idp.init(w, d, g));
  }

  updateConfig(o: userIDsConfig) {
    this._enabledIDs = o.ids || [];
    // Skip config updates if user has opted out (CCPA, DNT, GPC, or User ID opt-out)
    // This prevents loading partner scripts/URLs that could create IDs
    if (isOptedOut()) {
      return;
    }
    // update config for all children
    this._IDProviders
      .filter(idp => this._enabledIDs.includes(idp.getName()))
      .forEach(idp => idp.updateConfig(o[idp.getName()] || {}));
  }

  _isIDPEnabled(idpName: string = ''): boolean {
    // do not return data if user has opted out (CCPA, DNT, or User ID opt-out)
    // do not return if the IDProvider is not enabled
    return (
      idpName.length > 0 && this._enabledIDs.includes(idpName) && !isOptedOut()
    );
  }

  isUserIdEnabled(idpName: string = '', idpKey: string = ''): boolean {
    let ret = false;
    if (!this._isIDPEnabled(idpName)) {
      return ret;
    }
    const idp = this._IDProviders.find(v => v.getName() === idpName);
    if (!isValid(idp)) {
      return ret;
    }
    // $FlowFixMe
    ret = idp.isEnabled(idpKey);
    return ret;
  }

  /**
   * @param {string} idpName - IDProvider name
   * @param {string} idpKey - Additional key required to fetch individual ID values multi ID providers
   * @returns {string} ID value
   */
  getUserID(idpName: string = '', idpKey: string = ''): string | Array<string> {
    const ret = '';
    // do not return ID for CCPA opted out user
    // do not generate/return ID if DNT is enabled
    // do not return if the IDProvider is not enabled
    if (!this._isIDPEnabled(idpName)) {
      return ret;
    }
    const idp = this._IDProviders.find(v => v.getName() === idpName);
    if (!isValid(idp)) {
      return ret;
    }
    // $FlowFixMe
    return idp.getID(idpKey);
  }

  /**
   * @param {string} idpName - IDProvider name
   * @param {string} idpKey - Additional key required to fetch individual ID values multi ID providers
   * @returns {object} ID Ext data Object
   */
  getUserIDExt(idpName: string = '', idpKey: string = ''): anyObject {
    const ret = {};

    if (!this._isIDPEnabled(idpName)) {
      return ret;
    }
    const idp = this._IDProviders.find(v => v.getName() === idpName);
    if (!isValid(idp)) {
      return ret;
    }
    // $FlowFixMe
    return idp.getExt(idpKey);
  }
}

// export singleton object as default for this module
const idp: _idpmanager = new _idpmanager();
export default idp;
